import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getFileUrl } from '../../lib/api';

/**
 * Reusable full-screen image lightbox with keyboard + arrow navigation.
 * Props:
 *   images: string[] (storage paths)
 *   index: number (current index, or null/undefined when closed)
 *   onClose: () => void
 *   onIndexChange: (newIndex) => void
 */
export const Lightbox = ({ images = [], index = null, onClose, onIndexChange }) => {
  const isOpen = index !== null && index !== undefined && images.length > 0;
  const count = images.length;

  const goPrev = useCallback((e) => {
    if (e) e.stopPropagation();
    onIndexChange((index - 1 + count) % count);
  }, [index, count, onIndexChange]);

  const goNext = useCallback((e) => {
    if (e) e.stopPropagation();
    onIndexChange((index + 1) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && count > 1) goPrev();
      else if (e.key === 'ArrowRight' && count > 1) goNext();
    };
    window.addEventListener('keydown', handleKey);
    // Prevent background scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, count, goPrev, goNext, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label="Kapat"
        data-testid="lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>

      {count > 1 && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Önceki"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Sonraki"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <img
        src={getFileUrl(images[index])}
        alt=""
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg select-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />

      {count > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-white text-sm bg-white/10 px-4 py-1.5 rounded-full">
          {index + 1} / {count}
        </div>
      )}
    </div>
  );
};

export default Lightbox;
